/**
 * @file Reads My tasks: the forms assigned to the signed-in user, most
 * recently modified first, each with its current version read by path.
 * Consumer: `HomePageContent`, which calls it once and hands the read to My
 * tasks and Needs me, so both panels share one set of listeners. Without
 * `formsRead` or `formsWrite` no listener opens, because the rules deny the
 * list to an access-group-only reader. The `forms` store is standalone and
 * keyed on `uid`, so a shared store never mixes queries, and a failure is
 * kept beside the store it came from, so a new store never shows an old one.
 * The due-date field is `formDueDateField` of `parameters/analytics`; with no
 * such key no form has a due date. The read is loading until that document
 * has loaded, whatever the rights, so a `null` due field outside loading
 * always means none is set. Due dates read relative to the browser's day at
 * render time. A row links to Analytics only while `analyticsConfigured`,
 * which the caller reads once and passes in.
 */
import { useContext, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useSnapshot } from 'valtio';

import type { Form, FormVersion } from '#types';
import { analyticsRights, useAnalyticsParameters } from '@/analytics';
import { createCollectionStore, useStoreDocsByPaths } from '@/store';

import { UserContext } from '../../providers/UserContext';
import type { HomeMyTaskEntry, HomeMyTasksRead } from './HomeMyTasks.types';
import { homeMyTasks } from './homeMyTasks';
import { homeMyTasksQuery } from './homeMyTasksQuery';

export const useHomeMyTasks = (
  analyticsConfigured: boolean,
): HomeMyTasksRead => {
  const { user, userRights } = useContext(UserContext);
  const uid = user?.uid;
  const canRead =
    userRights.formsRead === true || userRights.formsWrite === true;
  const { parameters, loading: parametersLoading } = useAnalyticsParameters();
  const dueField = parameters?.formDueDateField ?? null;

  const store = useMemo(
    () =>
      createCollectionStore<Form>('forms', {
        standalone: true,
        queryOptions: uid ? homeMyTasksQuery(uid) : undefined,
      }),
    [uid],
  );
  const [failure, setFailure] = useState<{
    store: typeof store;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!uid || !canRead) return;
    void store.connect({
      onError: (error) => setFailure({ store, message: error.message }),
    });
    return () => store.disconnect();
  }, [store, uid, canRead]);

  const snapshot = useSnapshot(store.state);
  const forms = snapshot.docIds.flatMap((formId) => {
    const entry = snapshot.docs.get(formId);
    return entry ? [{ formId, form: entry.data }] : [];
  });
  const versionPath = (formId: string, versionId: string | undefined) =>
    versionId === undefined
      ? null
      : `forms/${formId}/formVersions/${versionId}`;
  const versions = useStoreDocsByPaths<FormVersion>(
    canRead
      ? forms.flatMap(({ formId, form }) => {
          const path = versionPath(formId, form.currentVersionId);
          return path === null ? [] : [path];
        })
      : [],
  );

  if (parametersLoading) return { dueField, status: 'loading' };
  if (!canRead) return { dueField, status: 'noRight' };
  if (failure !== null && failure.store === store)
    return { dueField, status: 'failed', message: failure.message };
  if (!uid || snapshot.loading || versions.loading)
    return { dueField, status: 'loading' };

  const entries: HomeMyTaskEntry[] = forms.map(({ formId, form }) => {
    const path = versionPath(formId, form.currentVersionId);
    return {
      formId,
      form,
      version: path === null ? undefined : versions.data.get(path),
    };
  });
  return {
    dueField,
    status: 'found',
    tasks: homeMyTasks({
      entries,
      dueField,
      rights: analyticsRights(userRights),
      analyticsConfigured,
      now: DateTime.local(),
    }),
  };
};
